import { Product } from "@transfr-inc/dashboard-components/constants";

import {
  XAPI_EVENT_TYPES,
  XAPI_OBJECT_TYPES,
  XAPI_TARGET_TYPES,
} from "./event-types";
import { xAPIEvent } from "./xapi-event.base";

// Re-exported so call sites keep importing the base class from this barrel.
export { xAPIEvent } from "./xapi-event.base";

/**
 * User logs into an application
 */
export class AuthenticatedEvent extends xAPIEvent {
  constructor(userId) {
    super(XAPI_EVENT_TYPES.AUTHENTICATED, {
      id: userId,
      name: "User Authentication",
      type: XAPI_OBJECT_TYPES.SESSION,
    });
  }
}

/**
 * User assigns a program to an organization
 */
export class ProgramAssignedToOrganizationEvent extends xAPIEvent {
  constructor(programId, programName, organizationId) {
    super(
      XAPI_EVENT_TYPES.ASSIGNED,
      {
        id: programId,
        name: programName,
        type: XAPI_OBJECT_TYPES.PROGRAM,
      },
      {
        targetType: XAPI_TARGET_TYPES.ORGANIZATION,
        targetId: organizationId,
      }
    );
  }
}

/**
 * User assigns an instructor to a classroom
 */
export class InstructorAssignedToClassroomEvent extends xAPIEvent {
  constructor(instructorUserId, classroomId) {
    super(
      XAPI_EVENT_TYPES.ASSIGNED,
      {
        id: instructorUserId,
        name: "Instructor",
        type: XAPI_OBJECT_TYPES.USER,
      },
      {
        targetType: XAPI_TARGET_TYPES.CLASSROOM,
        targetId: classroomId,
        classroomId: classroomId,
      }
    );
  }
}

/**
 * User assigns a learner to a classroom
 */
export class LearnerAssignedToClassroomEvent extends xAPIEvent {
  constructor(learnerUserId, classroomId) {
    super(
      XAPI_EVENT_TYPES.ASSIGNED,
      {
        id: learnerUserId,
        name: "Learner",
        type: XAPI_OBJECT_TYPES.USER,
      },
      {
        targetType: XAPI_TARGET_TYPES.CLASSROOM,
        targetId: classroomId,
        classroomId: classroomId,
      }
    );
  }
}

/**
 * User creates a new classroom
 */
export class ClassroomCreatedEvent extends xAPIEvent {
  constructor(classroomId, classroomName, classroomType, productId) {
    super(
      XAPI_EVENT_TYPES.CREATED,
      {
        id: classroomId,
        name: classroomName,
        type: XAPI_OBJECT_TYPES.CLASSROOM,
      },
      {
        classroomId: classroomId,
        classroomType: classroomType,
        productId: productId,
      }
    );
  }
}

/**
 * User assigns a program to a classroom
 */
export class ProgramAssignedToClassroomEvent extends xAPIEvent {
  constructor(programId, programName, classroomId) {
    super(
      XAPI_EVENT_TYPES.ASSIGNED,
      {
        id: programId,
        name: programName,
        type: XAPI_OBJECT_TYPES.PROGRAM,
      },
      {
        targetType: XAPI_TARGET_TYPES.CLASSROOM,
        targetId: classroomId,
        classroomId: classroomId,
        productId: Product.PROG.toString(),
      }
    );
  }
}

/**
 * User generates SimPass
 */
export class SimPassCreatedEvent extends xAPIEvent {
  constructor(learningExperienceId, simulationName, classroomId, productId) {
    super(
      XAPI_EVENT_TYPES.CREATED,
      {
        id: learningExperienceId,
        name: simulationName,
        type: XAPI_OBJECT_TYPES.SIMPASS,
      },
      {
        classroomId: classroomId,
        productId: productId,
      }
    );
  }
}

/**
 * Learner views the activity player
 */
export class ActivityPlayerViewedEvent extends xAPIEvent {
  constructor(pageId, pageName, classroomId, activityType) {
    super(
      XAPI_EVENT_TYPES.VIEWED,
      {
        id: pageId,
        name: pageName,
        type: XAPI_OBJECT_TYPES.PAGE,
      },
      {
        classroomId: classroomId,
        productId: Product.PROG.toString(),
        activityType: activityType,
      }
    );
  }
}

/**
 * User started storyline activity
 */
export class StorylineActivityStartedEvent extends xAPIEvent {
  constructor(contentId, activityName) {
    super(XAPI_EVENT_TYPES.ATTEMPTED, {
      id: contentId,
      name: activityName,
      type: XAPI_OBJECT_TYPES.STORYLINE_ACTIVITY,
    });
  }
}

/**
 * User completed storyline activity
 */
export class StorylineActivityCompletedEvent extends xAPIEvent {
  constructor(contentId, objectName, success = null, completed = null) {
    super(
      XAPI_EVENT_TYPES.COMPLETED,
      {
        id: contentId,
        name: objectName,
        type: XAPI_OBJECT_TYPES.STORYLINE_ACTIVITY,
      },
      {
        results: {
          success: success,
          completed: completed,
        },
      }
    );
  }
}

/**
 * User clicks preview classroom button
 */
export class PreviewClassroomButtonClickedEvent extends xAPIEvent {
  constructor(classroomId, programTemplateId, menuTemplateId, productId) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: classroomId,
        name: "Preview Classroom",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      {
        classroomId: classroomId,
        programTemplateId: programTemplateId,
        menuTemplateId: menuTemplateId,
        productId: productId,
      }
    );
  }
}

/**
 * User clicks next activity button
 */
export class NextActivityButtonClickedEvent extends xAPIEvent {
  constructor(buttonId, classroomId) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: buttonId,
        name: "Next Activity",
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      {
        classroomId: classroomId,
        productId: Product.PROG.toString(),
      }
    );
  }
}

/**
 * User clicks button
 */
export class ButtonClickedEvent extends xAPIEvent {
  constructor(id, name, options = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id,
        name,
        type: XAPI_OBJECT_TYPES.BUTTON,
      },
      options
    );
  }
}

/**
 * User viewed classroom page
 */
export class ClassroomPageViewedEvent extends xAPIEvent {
  constructor(pageId, pageName) {
    super(XAPI_EVENT_TYPES.VIEWED, {
      id: pageId,
      name: pageName,
      type: XAPI_OBJECT_TYPES.PAGE,
    });
  }
}

/**
 * User exported credentials
 */
export class CredentialsExportedEvent extends xAPIEvent {
  constructor(credentialId, credentialName, classroomId) {
    super(
      XAPI_EVENT_TYPES.EXPORTED,
      {
        id: credentialId,
        name: credentialName,
        type: XAPI_OBJECT_TYPES.CREDENTIAL,
      },
      {
        classroomId: classroomId,
        productId: Product.PROG.toString(),
      }
    );
  }
}

/**
 * User views a storyline slide
 */
export class StorylineSlideViewedEvent extends xAPIEvent {
  constructor(contentId, slideName) {
    super(XAPI_EVENT_TYPES.EXPERIENCED, {
      id: contentId,
      name: slideName,
      type: XAPI_OBJECT_TYPES.STORYLINE_SLIDE,
    });
  }
}

// Global events
export class ExpandAccordionEvent extends xAPIEvent {
  constructor(id, name, type = XAPI_OBJECT_TYPES.ACCORDION) {
    super(XAPI_EVENT_TYPES.SELECTED, {
      id: String(id),
      name: name,
      type,
    });
  }
}

export class SelectTabEvent extends xAPIEvent {
  constructor(id, name) {
    super(XAPI_EVENT_TYPES.SELECTED, {
      id: String(id),
      name,
      type: XAPI_OBJECT_TYPES.TAB,
    });
  }
}

export class ClickLinkEvent extends xAPIEvent {
  constructor(id, name, options = {}) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: String(id),
        name,
        type: XAPI_OBJECT_TYPES.HYPERLINK,
      },
      options
    );
  }
}

export class ClickNavbarItemEvent extends ClickLinkEvent {
  constructor(name) {
    const objectPathName = name?.replaceAll(" ", "_").toLowerCase();
    const objectId = `navigation_item_${objectPathName}`;

    super(objectId, name);
  }
}

export class SearchEvent extends xAPIEvent {
  constructor(id, name) {
    super(XAPI_EVENT_TYPES.SEARCHED, {
      id: String(id),
      name,
      type: XAPI_OBJECT_TYPES.SEARCH_BAR,
    });
  }
}

export class ClassroomArchivedEvent extends xAPIEvent {
  constructor(
    classroomId,
    classroomName,
    source,
    learnersAlsoArchived,
    learnerArchiveCount,
    rostered,
    batchId,
    isBatchEvent
  ) {
    super(
      XAPI_EVENT_TYPES.ARCHIVED,
      {
        id: String(classroomId),
        name: classroomName,
        type: XAPI_OBJECT_TYPES.CLASSROOM,
      },
      {
        source,
        learnersAlsoArchived,
        learnerArchiveCount,
        rostered,
        batchId,
        isBatchEvent,
      }
    );
  }
}

export class ClassroomReactivatedEvent extends xAPIEvent {
  constructor(
    classroomId,
    classroomName,
    source,
    timeInArchiveDays,
    rostered,
    batchId,
    isBatchEvent
  ) {
    super(
      XAPI_EVENT_TYPES.REACTIVATED,
      {
        id: String(classroomId),
        name: classroomName,
        type: XAPI_OBJECT_TYPES.CLASSROOM,
      },
      {
        source,
        timeInArchiveDays,
        rostered,
        batchId,
        isBatchEvent,
      }
    );
  }
}

export class ClassroomDeletedEvent extends xAPIEvent {
  constructor(
    classroomId,
    classroomName,
    timeInArchiveDays,
    batchId,
    isBatchEvent
  ) {
    super(
      XAPI_EVENT_TYPES.DELETED,
      {
        id: String(classroomId),
        name: classroomName,
        type: XAPI_OBJECT_TYPES.CLASSROOM,
      },
      {
        timeInArchiveDays,
        batchId,
        isBatchEvent,
      }
    );
  }
}

export class SoleClassroomWarningViewedEvent extends xAPIEvent {
  constructor() {
    super(
      XAPI_EVENT_TYPES.VIEWED,
      {
        id: "sole_classroom_archive_warning",
        name: "Sole Classroom Archive Warning",
        type: XAPI_OBJECT_TYPES.WARNING,
      },
      {
        warningType: "sole_classroom_archive",
      }
    );
  }
}

export class SoleClassroomWarningSelectedEvent extends xAPIEvent {
  constructor(affectedLearnerCount, isBulkOperation, userProceeded) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: "sole_classroom_archive_warning",
        name: "Sole Classroom Archive Warning",
        type: XAPI_OBJECT_TYPES.WARNING,
      },
      {
        warningType: "sole_classroom_archive",
        affectedLearnerCount,
        isBulkOperation,
        userProceeded,
      }
    );
  }
}

export class ClassroomStatusFilterSelectedEvent extends xAPIEvent {
  constructor(filterValue, previousFilterValue, classroomCountInView) {
    super(
      XAPI_EVENT_TYPES.SELECTED,
      {
        id: "classroom_status_filter",
        name: "Classroom Status Filter",
        type: XAPI_OBJECT_TYPES.FILTER,
      },
      {
        filterValue,
        previousFilterValue,
        classroomCountInView,
      }
    );
  }
}

export class LearnerArchivedEvent extends xAPIEvent {
  constructor(
    userId,
    username,
    source,
    triggeredByClassroomArchive,
    rostered,
    batchId,
    isBatchEvent
  ) {
    super(
      XAPI_EVENT_TYPES.ARCHIVED,
      {
        id: String(userId),
        name: username,
        type: XAPI_OBJECT_TYPES.USER,
      },
      {
        source,
        triggeredByClassroomArchive,
        rostered,
        batchId,
        isBatchEvent,
      }
    );
  }
}

export class LearnerReactivatedEvent extends xAPIEvent {
  constructor(userId, username, timeInArchiveDays, batchId, isBatchEvent) {
    super(
      XAPI_EVENT_TYPES.REACTIVATED,
      {
        id: String(userId),
        name: username,
        type: XAPI_OBJECT_TYPES.USER,
      },
      {
        timeInArchiveDays,
        batchId,
        isBatchEvent,
      }
    );
  }
}

// Outcomes dashboard events live in a sibling module and are re-exported here so
// call sites keep importing every xAPI event from this one barrel.
export * from "./xapi-events.outcomes";
